import api from './api';
import {
  Account,
  JournalEntry,
  JournalEntryFormData,
  AccountsResponse,
  JournalEntriesResponse,
  TrialBalanceResponse,
} from '../types';

interface ReportParams {
  start_date?: string;
  end_date?: string;
  as_of_date?: string;
}

interface ProfitLossReport {
  revenue: Array<{ account_name: string; amount: number }>;
  expenses: Array<{ account_name: string; amount: number }>;
  total_revenue: number;
  total_expenses: number;
  net_income: number;
  period_start: string;
  period_end: string;
}

interface BalanceSheetReport {
  assets: Array<{ account_name: string; amount: number }>;
  liabilities: Array<{ account_name: string; amount: number }>;
  equity: Array<{ account_name: string; amount: number }>;
  total_assets: number;
  total_liabilities: number;
  total_equity: number;
  as_of_date: string;
}

const accountingService = {
  // Chart of Accounts
  getAccounts: async (): Promise<AccountsResponse> => {
    const response = await api.get<AccountsResponse>('/accounting/accounts');
    return response.data;
  },

  getAccount: async (accountId: number): Promise<{ account: Account }> => {
    const response = await api.get<{ account: Account }>(`/accounting/accounts/${accountId}`);
    return response.data;
  },

  createAccount: async (accountData: Partial<Account>): Promise<{ account: Account }> => {
    const response = await api.post<{ account: Account }>('/accounting/accounts', accountData);
    return response.data;
  },

  // Journal Entries
  getJournalEntries: async (params: Record<string, any> = {}): Promise<JournalEntriesResponse> => {
    const response = await api.get<JournalEntriesResponse>('/accounting/journal', { params });
    return response.data;
  },

  getJournalEntry: async (entryId: number): Promise<{ entry: JournalEntry }> => {
    const response = await api.get<{ entry: JournalEntry }>(`/accounting/journal/${entryId}`);
    return response.data;
  },

  createJournalEntry: async (entryData: JournalEntryFormData): Promise<{ entry: JournalEntry }> => {
    const response = await api.post<{ entry: JournalEntry }>('/accounting/journal', entryData);
    return response.data;
  },

  postJournalEntry: async (entryId: number): Promise<{ entry: JournalEntry }> => {
    const response = await api.put<{ entry: JournalEntry }>(`/accounting/journal/${entryId}/post`);
    return response.data;
  },

  // Reports
  getTrialBalance: async (params: ReportParams = {}): Promise<TrialBalanceResponse> => {
    const response = await api.get<TrialBalanceResponse>('/accounting/trial-balance', { params });
    return response.data;
  },

  getProfitLoss: async (params: ReportParams = {}): Promise<ProfitLossReport> => {
    const response = await api.get<ProfitLossReport>('/accounting/reports/profit-loss', { params });
    return response.data;
  },

  getBalanceSheet: async (params: ReportParams = {}): Promise<BalanceSheetReport> => {
    const response = await api.get<BalanceSheetReport>('/accounting/reports/balance-sheet', { params });
    return response.data;
  },
};

export default accountingService;
